import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { DatePicker, message, Modal, Popconfirm, Select, Table, TableProps, Tooltip } from "antd";
import dayjs from "dayjs";
import { FaEdit, FaEye, FaSearch, FaTrash, FaUndo } from "react-icons/fa";
import Spinner from "../../components/shared/Spinner/Spinner";
import MainButton from "../../components/shared/MainButton/MainButton";
import { DeliveryBranchItemType, UpdateUserPayload, UserItemType, UsersFilterParams } from "../../interfaces/admin.interface";
import { UsersService } from "../../services/users.service";
import { DeliveryBranchesService } from "../../services/delivery_branches.service";
import styles from "./Users.module.scss";

interface InitialFilterState {
  name: string;
  surname: string;
  email: string;
  number: string;
  start_date: string;
  end_date: string;
  fin: string;
  user_id: string;
  gender: string;
  branch_id: string;
}

const initialFilters: InitialFilterState = {
  name: "",
  surname: "",
  email: "",
  number: "",
  start_date: "",
  end_date: "",
  fin: "",
  user_id: "",
  gender: "",
  branch_id: "",
};

interface EditUserState {
  id: number;
  name: string;
  surname: string;
  fin: string;
  email: string;
  branch_id: string;
  address: string;
  number: string;
  number_prefix: string;
  gender: string;
}

const initialEditState: EditUserState = {
  id: 0,
  name: "",
  surname: "",
  fin: "",
  email: "",
  branch_id: "",
  address: "",
  number: "",
  number_prefix: "994",
  gender: "",
};

const Users = () => {
  const [usersList, setUsersList] = useState<UserItemType[]>([]);
  const [branchesList, setBranchesList] = useState<DeliveryBranchItemType[]>([]);
  const [isBranchesLoading, setIsBranchesLoading] = useState<boolean>(false);
  const [selectedBranch, setSelectedBranch] = useState<{ value: string; label: string } | null>(null);
  const branchSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filterValues, setFilterValues] = useState<InitialFilterState>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<InitialFilterState>(initialFilters);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedUser, setSelectedUser] = useState<UserItemType | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);

  // Edit User State
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isEditingLoading, setIsEditingLoading] = useState<boolean>(false);
  const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false);
  const [editFormValues, setEditFormValues] = useState<EditUserState>(initialEditState);
  const [editSelectedBranch, setEditSelectedBranch] = useState<{ value: string; label: string } | null>(null);
  const [editErrors, setEditErrors] = useState<Record<string, string[]>>({});
  const [originalEditValues, setOriginalEditValues] = useState<EditUserState>(initialEditState);

  const hasFormChanged = useMemo(() => {
    return (
      editFormValues.name !== originalEditValues.name ||
      editFormValues.surname !== originalEditValues.surname ||
      editFormValues.fin !== originalEditValues.fin ||
      editFormValues.email !== originalEditValues.email ||
      editFormValues.branch_id !== originalEditValues.branch_id ||
      editFormValues.address !== originalEditValues.address ||
      editFormValues.number !== originalEditValues.number ||
      editFormValues.number_prefix !== originalEditValues.number_prefix ||
      editFormValues.gender !== originalEditValues.gender
    );
  }, [editFormValues, originalEditValues]);

  const handleFetchUsers = async (page: number, filtersToApply: InitialFilterState) => {
    setIsLoading(true);

    const params: UsersFilterParams = {
      page,
      per_page: pageSize,
      ...filtersToApply,
    };

    const response = await UsersService.getUsers(params);

    if (!response?.data?.status) {
      setIsLoading(false);
      return;
    }

    const paginationData = response.data.data;
    const items = Array.isArray(paginationData?.data) ? paginationData.data : [];

    setUsersList(items.map((item: UserItemType, index: number) => ({
      ...item,
      key: item.id || index + 1,
    })));
    setTotalCount(paginationData?.total || 0);
    setCurrentPage(paginationData?.current_page || page);
    setIsLoading(false);
  };

  const handleFetchBranches = async (searchQuery?: string) => {
    setIsBranchesLoading(true);
    const params = searchQuery ? { search: searchQuery } : undefined;
    const res = await DeliveryBranchesService.getDeliveryBranches(params);
    const rawData = res?.data?.data || res?.data;
    const items = Array.isArray(rawData) ? rawData : (Array.isArray(rawData?.data) ? rawData.data : []);
    setBranchesList(items);
    setIsBranchesLoading(false);
  };

  const handleSearchBranches = (searchValue: string) => {
    if (branchSearchTimeoutRef.current) {
      clearTimeout(branchSearchTimeoutRef.current);
    }
    branchSearchTimeoutRef.current = setTimeout(() => {
      handleFetchBranches(searchValue.trim());
    }, 350);
  };

  const formatBranchLabel = (branch: DeliveryBranchItemType) => {
    const postIndex = branch.post_index ? branch.post_index.trim() : "";
    const title = (branch.name || branch.branch_name || branch.title || branch.address || "").trim();

    if (postIndex && title) {
      return `${postIndex} (${title})`;
    }
    if (postIndex) {
      return postIndex;
    }
    if (title) {
      return title;
    }
    return `Filial #${branch.id}`;
  };

  useEffect(() => {
    handleFetchUsers(1, initialFilters);
    handleFetchBranches();

    return () => {
      if (branchSearchTimeoutRef.current) {
        clearTimeout(branchSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (field: keyof InitialFilterState, value: string) => {
    setFilterValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleFilterSubmit = () => {
    setCurrentPage(1);
    setAppliedFilters(filterValues);
    handleFetchUsers(1, filterValues);
  };

  const handleBranchChange = (option: any) => {
    if (!option) {
      setSelectedBranch(null);
      handleInputChange("branch_id", "");
      handleFetchBranches();
      return;
    }
    setSelectedBranch({ value: String(option.value), label: String(option.label) });
    handleInputChange("branch_id", String(option.value));
  };

  const handleResetFilters = () => {
    setSelectedBranch(null);
    setFilterValues(initialFilters);
    setAppliedFilters(initialFilters);
    setCurrentPage(1);
    handleFetchUsers(1, initialFilters);
    handleFetchBranches();
  };

  const handlePageChange = (page: number, newPageSize?: number) => {
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }
    setCurrentPage(page);
    handleFetchUsers(page, appliedFilters);
  };

  // View User Detail
  const handleOpenDetailModal = async (record: UserItemType) => {
    setSelectedUser(record);
    setIsDetailModalOpen(true);
    const res = await UsersService.getUserById(record.id);
    if (res?.data?.data) {
      setSelectedUser(res.data.data);
    }
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedUser(null);
  };

  const handleDisplayBackendErrors = (res: any, fallbackMessage: string) => {
    const errorData = res?.error?.response?.data || res?.data;

    if (errorData?.errors && typeof errorData.errors === "object" && Object.keys(errorData.errors).length > 0) {
      setEditErrors(errorData.errors);

      const knownFields = [
        "name",
        "surname",
        "fin",
        "email",
        "number",
        "number_prefix",
        "gender",
        "branch_id",
        "address",
      ];
      const generalErrors: string[] = [];

      Object.entries(errorData.errors).forEach(([field, val]: [string, any]) => {
        if (!knownFields.includes(field)) {
          if (Array.isArray(val)) {
            val.forEach((m) => typeof m === "string" && generalErrors.push(m));
          } else if (typeof val === "string") {
            generalErrors.push(val);
          }
        }
      });

      if (generalErrors.length > 0) {
        generalErrors.forEach((msg) => message.error(msg));
      }
      return;
    }

    if (typeof errorData?.message === "string" && errorData.message.trim() !== "") {
      message.error(errorData.message);
      return;
    }

    message.error(fallbackMessage);
  };

  // Edit User Handlers
  const handleOpenEditModal = async (record: UserItemType) => {
    setIsEditModalOpen(true);
    setIsEditingLoading(true);
    setEditErrors({});

    const res = await UsersService.getUserById(record.id);
    const data: UserItemType = res?.data?.data || record;

    const initialValues: EditUserState = {
      id: data.id,
      name: data.name || "",
      surname: data.surname || "",
      fin: data.fin || "",
      email: data.email || "",
      branch_id: data.branch_id ? String(data.branch_id) : "",
      address: data.address || "",
      number: data.number || "",
      number_prefix: data.number_prefix ? String(data.number_prefix) : "994",
      gender: data.gender || "",
    };

    setEditFormValues(initialValues);
    setOriginalEditValues(initialValues);

    if (data.branch_id) {
      const postIndex = data.post_index ? data.post_index.trim() : "";
      const branchName = data.branch_name ? data.branch_name.trim() : "";
      const label = postIndex && branchName ? `${postIndex} (${branchName})` : (branchName || postIndex || `Filial #${data.branch_id}`);
      setEditSelectedBranch({ value: String(data.branch_id), label });
    } else {
      setEditSelectedBranch(null);
    }

    setIsEditingLoading(false);
  };

  const handleEditInputChange = (field: keyof EditUserState, value: any) => {
    setEditFormValues((prev) => ({ ...prev, [field]: value }));
    if (editErrors[field]) {
      setEditErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleEditBranchChange = (option: any) => {
    if (!option) {
      setEditSelectedBranch(null);
      handleEditInputChange("branch_id", "");
      return;
    }
    setEditSelectedBranch({ value: String(option.value), label: String(option.label) });
    handleEditInputChange("branch_id", String(option.value));
  };

  const handleUpdateUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingEdit(true);

    const payload: UpdateUserPayload = {
      name: editFormValues.name.trim(),
      surname: editFormValues.surname.trim(),
      fin: editFormValues.fin.trim(),
      email: editFormValues.email.trim(),
      branch_id: editFormValues.branch_id ? Number(editFormValues.branch_id) : null,
      address: editFormValues.address.trim(),
      number: editFormValues.number.trim(),
      number_prefix: editFormValues.number_prefix,
      gender: editFormValues.gender || null,
    };

    const res = await UsersService.updateUser(editFormValues.id, payload);
    setIsSavingEdit(false);

    const errorData = res?.error?.response?.data || res?.data;
    const isSuccess = (res?.data?.status === true || res?.data?.status === "success") && !errorData?.errors && errorData?.status !== "error";

    if (isSuccess) {
      message.success("İstifadəçi məlumatları uğurla yeniləndi");
      setIsEditModalOpen(false);
      setEditErrors({});
      handleFetchUsers(currentPage, appliedFilters);
      return;
    }

    handleDisplayBackendErrors(res, "Yenilənmə zamanı xəta baş verdi");
  };

  // Delete User Handler
  const handleDeleteUser = async (id: number) => {
    const res = await UsersService.deleteUser(id);
    const errorData = res?.error?.response?.data || res?.data;
    const isSuccess = (res?.data?.status === true || res?.data?.status === "success" || res?.status === 200) && errorData?.status !== "error" && !errorData?.errors;

    if (isSuccess) {
      message.success("İstifadəçi uğurla silindi");
      handleFetchUsers(currentPage, appliedFilters);
      return;
    }

    handleDisplayBackendErrors(res, "Silinmə zamanı xəta baş verdi");
  };

  const handleKeyDownFilter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    handleFilterSubmit();
  };

  const handleDisabledStartDate = (current: dayjs.Dayjs) => {
    if (!current) return false;
    if (current > dayjs().endOf("day")) return true;
    if (filterValues.end_date && current > dayjs(filterValues.end_date).endOf("day")) return true;
    return false;
  };

  const handleDisabledEndDate = (current: dayjs.Dayjs) => {
    if (!current) return false;
    if (current > dayjs().endOf("day")) return true;
    if (filterValues.start_date && current < dayjs(filterValues.start_date).startOf("day")) return true;
    return false;
  };

  const tableColumns: TableProps<UserItemType>["columns"] = [
    {
      title: "№",
      key: "row_number",
      width: 60,
      align: "center",
      render: (_, __, index: number) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "ID",
      dataIndex: "id",
      width: 75,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Ad",
      dataIndex: "name",
      render: (text) => text || "-",
    },
    {
      title: "Soyad",
      dataIndex: "surname",
      render: (text) => text || "-",
    },
    {
      title: "E-poçt",
      dataIndex: "email",
      render: (email) => (email ? (
        <a
          href={`mailto:${email}`}
          className={styles.link_cell}
          aria-label={`Send email to ${email}`}
          tabIndex={0}
        >
          {email}
        </a>
      ) : "-"),
    },
    {
      title: "Nömrə",
      dataIndex: "number",
      render: (_, record) => {
        if (!record?.number) return "-";
        const prefix = record.number_prefix ? `+${record.number_prefix}` : "";
        const fullNumber = `${prefix} ${record.number}`.trim();
        return (
          <a
            href={`tel:${record.number_prefix || ""}${record.number}`}
            className={styles.link_cell}
            aria-label={`Call ${fullNumber}`}
            tabIndex={0}
          >
            {fullNumber}
          </a>
        );
      },
    },
    {
      title: "FIN",
      dataIndex: "fin",
      render: (text) => (
        <span className={styles.fin_badge}>{text || "-"}</span>
      ),
    },
    {
      title: "Cins",
      dataIndex: "gender",
      width: 90,
      render: (gender) => {
        if (gender === "M") return <span className={styles.gender_male}>Kişi</span>;
        if (gender === "F") return <span className={styles.gender_female}>Qadın</span>;
        return "-";
      },
    },
    {
      title: "Filial",
      dataIndex: "branch_name",
      render: (text) => (text ? text.trim() : "-"),
    },
    {
      title: "Ünvan",
      dataIndex: "address",
      ellipsis: true,
      render: (text) => text || "-",
    },
    {
      title: "Poçt indeksi",
      dataIndex: "post_index",
      width: 110,
      render: (text) => text || "-",
    },
    {
      title: "Qeydiyyat tarixi",
      dataIndex: "created_at",
      width: 155,
      render: (date: string | null) => (date ? dayjs(date).format("YYYY-MM-DD HH:mm") : "-"),
      sorter: (a, b) => {
        if (!a.created_at) return -1;
        if (!b.created_at) return 1;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      },
    },
    {
      title: "Əməliyyatlar",
      key: "actions",
      width: 120,
      align: "center",
      render: (_, record) => (
        <div className={styles.action_buttons}>
          <button
            type="button"
            onClick={() => handleOpenDetailModal(record)}
            className={`${styles.action_btn} ${styles.view_btn}`}
            aria-label={`Bax: ${record.name} ${record.surname || ""}`}
            tabIndex={0}
          >
            <FaEye size={15} />
          </button>
          <button
            type="button"
            onClick={() => handleOpenEditModal(record)}
            className={`${styles.action_btn} ${styles.edit_btn}`}
            aria-label={`Düzəliş et: ${record.name} ${record.surname || ""}`}
            tabIndex={0}
          >
            <FaEdit size={15} />
          </button>
          <Popconfirm
            title="Bu istifadəçini silmək istədiyinizə əminsiniz?"
            onConfirm={() => handleDeleteUser(record.id)}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <button
              type="button"
              className={`${styles.action_btn} ${styles.delete_btn}`}
              aria-label={`Sil: ${record.name} ${record.surname || ""}`}
              tabIndex={0}
            >
              <FaTrash size={14} />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div id="pages">
      <Helmet>
        <title>1412 | İstifadəçilər</title>
      </Helmet>

      <div className={styles.users_container}>
        <div className={styles.page_header}>
          <h1 className={styles.page_title}>İstifadəçilər</h1>
          <span className={styles.total_badge}>
            Toplam istifadəçi: <strong>{totalCount}</strong>
          </span>
        </div>

        {/* Filter Section */}
        <section aria-label="İstifadəçi filtrləri" className={styles.filter_card}>
          <div className={styles.filter_grid}>
            <div className={styles.filter_item}>
              <label htmlFor="filter-name">Ad</label>
              <input
                id="filter-name"
                type="text"
                value={filterValues.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                onKeyDown={handleKeyDownFilter}
                placeholder="Ad daxil edin"
                className={styles.filter_input}
                tabIndex={0}
                aria-label="Ad üzrə axtarış"
              />
            </div>

            <div className={styles.filter_item}>
              <label htmlFor="filter-surname">Soyad</label>
              <input
                id="filter-surname"
                type="text"
                value={filterValues.surname}
                onChange={(e) => handleInputChange("surname", e.target.value)}
                onKeyDown={handleKeyDownFilter}
                placeholder="Soyad daxil edin"
                className={styles.filter_input}
                tabIndex={0}
                aria-label="Soyad üzrə axtarış"
              />
            </div>

            <div className={styles.filter_item}>
              <label htmlFor="filter-email">E-poçt</label>
              <input
                id="filter-email"
                type="email"
                value={filterValues.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                onKeyDown={handleKeyDownFilter}
                placeholder="E-poçt daxil edin"
                className={styles.filter_input}
                tabIndex={0}
                aria-label="E-poçt üzrə axtarış"
              />
            </div>

            <div className={styles.filter_item}>
              <label htmlFor="filter-number">Nömrə</label>
              <input
                id="filter-number"
                type="text"
                value={filterValues.number}
                onChange={(e) => handleInputChange("number", e.target.value)}
                onKeyDown={handleKeyDownFilter}
                placeholder="Nömrə daxil edin"
                className={styles.filter_input}
                tabIndex={0}
                aria-label="Nömrə üzrə axtarış"
              />
            </div>

            <div className={styles.filter_item}>
              <label htmlFor="filter-fin">FIN</label>
              <input
                id="filter-fin"
                type="text"
                value={filterValues.fin}
                onChange={(e) => handleInputChange("fin", e.target.value.toUpperCase())}
                onKeyDown={handleKeyDownFilter}
                placeholder="FIN daxil edin"
                className={`${styles.filter_input} ${styles.uppercase_input}`}
                tabIndex={0}
                aria-label="FIN kod üzrə axtarış"
              />
            </div>

            <div className={styles.filter_item}>
              <label htmlFor="filter-user-id">İstifadəçi ID</label>
              <input
                id="filter-user-id"
                type="text"
                value={filterValues.user_id}
                onChange={(e) => handleInputChange("user_id", e.target.value)}
                onKeyDown={handleKeyDownFilter}
                placeholder="ID daxil edin"
                className={styles.filter_input}
                tabIndex={0}
                aria-label="İstifadəçi ID üzrə axtarış"
              />
            </div>

            <div className={styles.filter_item}>
              <label htmlFor="filter-gender">Cins</label>
              <Select
                id="filter-gender"
                value={filterValues.gender || undefined}
                onChange={(val) => handleInputChange("gender", val || "")}
                placeholder="Bütün cinslər"
                allowClear
                className={styles.filter_select}
                options={[
                  { label: "Kişi", value: "M" },
                  { label: "Qadın", value: "F" },
                ]}
              />
            </div>

            <div className={styles.filter_item}>
              <label htmlFor="filter-branch">Filial</label>
              <Select
                id="filter-branch"
                labelInValue
                value={selectedBranch || undefined}
                onChange={handleBranchChange}
                placeholder="Bütün filiallar"
                allowClear
                showSearch
                filterOption={false}
                onSearch={handleSearchBranches}
                loading={isBranchesLoading}
                className={styles.filter_select}
                notFoundContent={isBranchesLoading ? "Yüklənir..." : "Filial tapılmadı"}
                options={branchesList.map((branch) => ({
                  label: formatBranchLabel(branch),
                  value: String(branch.id),
                }))}
              />
            </div>

            <div className={styles.filter_item}>
              <label>Başlanğıc tarixi</label>
              <DatePicker
                className={styles.filter_date}
                placeholder="Başlanğıc tarixi"
                format="YYYY-MM-DD"
                disabledDate={handleDisabledStartDate}
                value={filterValues.start_date ? dayjs(filterValues.start_date) : null}
                onChange={(_, dateString) => handleInputChange("start_date", typeof dateString === "string" ? dateString : "")}
              />
            </div>

            <div className={styles.filter_item}>
              <label>Bitmə tarixi</label>
              <DatePicker
                className={styles.filter_date}
                placeholder="Bitmə tarixi"
                format="YYYY-MM-DD"
                disabledDate={handleDisabledEndDate}
                value={filterValues.end_date ? dayjs(filterValues.end_date) : null}
                onChange={(_, dateString) => handleInputChange("end_date", typeof dateString === "string" ? dateString : "")}
              />
            </div>
          </div>

          <div className={styles.filter_actions}>
            <button
              type="button"
              onClick={handleResetFilters}
              className={styles.reset_btn}
              tabIndex={0}
              aria-label="Filtrləri sıfırla"
            >
              <FaUndo size={13} />
              Sıfırla
            </button>
            <MainButton onClick={handleFilterSubmit}>
              <div className={styles.search_btn_content}>
                <FaSearch size={13} />
                <span>Axtar</span>
              </div>
            </MainButton>
          </div>
        </section>

        {/* Table Section */}
        <section aria-label="İstifadəçi cədvəli" className={styles.table_card}>
          {isLoading ? (
            <div style={{ padding: "64px 0", display: "flex", justifyContent: "center" }}>
              <Spinner />
            </div>
          ) : (
            <div className={styles.table_wrapper}>
              <Table<UserItemType>
                columns={tableColumns}
                dataSource={usersList}
                rowKey={(record) => String(record.id)}
                scroll={{ x: 1100 }}
                locale={{
                  emptyText: "Məlumat tapılmadı",
                  triggerDesc: "Azalan sıra ilə düz",
                  triggerAsc: "Artan sıra ilə düz",
                  cancelSort: "Sıralamanı ləğv et",
                }}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalCount,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  locale: { items_per_page: "" },
                  onChange: handlePageChange,
                  showTotal: (total, range) => (
                    <span className={styles.total_text}>
                      Göstərilir: {range[0]}-{range[1]} / Cəmi: {total}
                    </span>
                  ),
                }}
              />
            </div>
          )}
        </section>
      </div>

      {/* User Details Modal */}
      <Modal
        title={<div className={styles.modal_title}>İstifadəçi Məlumatları #{selectedUser?.id}</div>}
        open={isDetailModalOpen}
        onCancel={handleCloseDetailModal}
        width={620}
        footer={[
          <button
            key="close"
            type="button"
            onClick={handleCloseDetailModal}
            className={styles.modal_close_btn}
            tabIndex={0}
            aria-label="Bağla"
          >
            Bağla
          </button>,
        ]}
      >
        {selectedUser && (
          <div>
            <div className={styles.modal_user_header}>
              <div className={styles.user_avatar}>
                {selectedUser.name?.charAt(0)?.toUpperCase()}
                {selectedUser.surname?.charAt(0)?.toUpperCase() || ""}
              </div>
              <div className={styles.user_headline}>
                <h3>{selectedUser.name} {selectedUser.surname || ""}</h3>
                <span className={styles.user_sub_id}>İstifadəçi ID: #{selectedUser.id}</span>
              </div>
            </div>

            <div className={styles.info_list}>
              <div className={styles.info_row}>
                <span className={styles.info_label}>Ad və Soyad:</span>
                <span className={styles.info_value}>{selectedUser.name} {selectedUser.surname || "-"}</span>
              </div>
              <div className={styles.info_row}>
                <span className={styles.info_label}>FIN Kod:</span>
                <span className={styles.info_value}>
                  <span className={styles.fin_badge}>{selectedUser.fin || "-"}</span>
                </span>
              </div>
              <div className={styles.info_row}>
                <span className={styles.info_label}>E-poçt:</span>
                <span className={styles.info_value}>
                  {selectedUser.email ? (
                    <a href={`mailto:${selectedUser.email}`} className={styles.link_cell}>
                      {selectedUser.email}
                    </a>
                  ) : "-"}
                </span>
              </div>
              <div className={styles.info_row}>
                <span className={styles.info_label}>Telefon:</span>
                <span className={styles.info_value}>
                  {selectedUser.number ? (
                    <a href={`tel:${selectedUser.number_prefix || ""}${selectedUser.number}`} className={styles.link_cell}>
                      {selectedUser.number_prefix ? `+${selectedUser.number_prefix} ` : ""}{selectedUser.number}
                    </a>
                  ) : "-"}
                </span>
              </div>
              <div className={styles.info_row}>
                <span className={styles.info_label}>Cins:</span>
                <span className={styles.info_value}>
                  {selectedUser.gender === "M" ? (
                    <span className={styles.gender_male}>Kişi</span>
                  ) : selectedUser.gender === "F" ? (
                    <span className={styles.gender_female}>Qadın</span>
                  ) : "-"}
                </span>
              </div>
              <div className={styles.info_row}>
                <span className={styles.info_label}>Filial:</span>
                <span className={styles.info_value}>{selectedUser.branch_name ? selectedUser.branch_name.trim() : "-"}</span>
              </div>
              <div className={styles.info_row}>
                <span className={styles.info_label}>Poçt indeksi:</span>
                <span className={styles.info_value}>{selectedUser.post_index || "-"}</span>
              </div>
              <div className={styles.info_row}>
                <span className={styles.info_label}>Ünvan:</span>
                <span className={styles.info_value}>{selectedUser.address || "-"}</span>
              </div>
              <div className={styles.info_row}>
                <span className={styles.info_label}>Qeydiyyat tarixi:</span>
                <span className={styles.info_value}>
                  {selectedUser.created_at ? dayjs(selectedUser.created_at).format("YYYY-MM-DD HH:mm") : "-"}
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title={<div className={styles.modal_title}>İstifadəçiyə Düzəliş Et #{editFormValues.id}</div>}
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        footer={null}
        width={650}
      >
        {isEditingLoading ? (
          <div style={{ padding: "40px 0", display: "flex", justifyContent: "center" }}>
            <Spinner />
          </div>
        ) : (
          <form className={styles.edit_form} onSubmit={handleUpdateUserSubmit}>
            <div className={styles.edit_grid}>
              <div className={styles.form_item}>
                <label htmlFor="edit-name">Ad *</label>
                <input
                  id="edit-name"
                  type="text"
                  value={editFormValues.name}
                  onChange={(e) => handleEditInputChange("name", e.target.value)}
                  placeholder="Ad"
                  className={`${styles.form_input} ${editErrors.name ? styles.input_error : ""}`}
                  tabIndex={0}
                />
                {editErrors.name && <span className={styles.field_error}>{editErrors.name[0]}</span>}
              </div>

              <div className={styles.form_item}>
                <label htmlFor="edit-surname">Soyad</label>
                <input
                  id="edit-surname"
                  type="text"
                  value={editFormValues.surname}
                  onChange={(e) => handleEditInputChange("surname", e.target.value)}
                  placeholder="Soyad"
                  className={`${styles.form_input} ${editErrors.surname ? styles.input_error : ""}`}
                  tabIndex={0}
                />
                {editErrors.surname && <span className={styles.field_error}>{editErrors.surname[0]}</span>}
              </div>

              <div className={styles.form_item}>
                <label htmlFor="edit-fin">FIN</label>
                <input
                  id="edit-fin"
                  type="text"
                  value={editFormValues.fin}
                  onChange={(e) => handleEditInputChange("fin", e.target.value.toUpperCase())}
                  placeholder="FIN kod"
                  className={`${styles.form_input} ${styles.uppercase} ${editErrors.fin ? styles.input_error : ""}`}
                  tabIndex={0}
                />
                {editErrors.fin && <span className={styles.field_error}>{editErrors.fin[0]}</span>}
              </div>

              <div className={styles.form_item}>
                <label htmlFor="edit-email">E-poçt *</label>
                <input
                  id="edit-email"
                  type="email"
                  value={editFormValues.email}
                  onChange={(e) => handleEditInputChange("email", e.target.value)}
                  placeholder="E-poçt"
                  className={`${styles.form_input} ${editErrors.email ? styles.input_error : ""}`}
                  tabIndex={0}
                />
                {editErrors.email && <span className={styles.field_error}>{editErrors.email[0]}</span>}
              </div>

              <div className={styles.form_item}>
                <label htmlFor="edit-number">Telefon</label>
                <div className={styles.phone_wrapper}>
                  <input
                    type="text"
                    value={editFormValues.number_prefix}
                    onChange={(e) => handleEditInputChange("number_prefix", e.target.value)}
                    className={styles.prefix_input}
                    title="Ölkə prefiksi"
                    tabIndex={0}
                  />
                  <input
                    id="edit-number"
                    type="text"
                    value={editFormValues.number}
                    onChange={(e) => handleEditInputChange("number", e.target.value)}
                    placeholder="Telefon nömrəsi"
                    className={`${styles.phone_input} ${editErrors.number ? styles.input_error : ""}`}
                    tabIndex={0}
                  />
                </div>
                {editErrors.number && <span className={styles.field_error}>{editErrors.number[0]}</span>}
              </div>

              <div className={styles.form_item}>
                <label htmlFor="edit-gender">Cins</label>
                <Select
                  id="edit-gender"
                  value={editFormValues.gender || undefined}
                  onChange={(val) => handleEditInputChange("gender", val || "")}
                  placeholder="Cins seçin"
                  allowClear
                  className={styles.form_select}
                  status={editErrors.gender ? "error" : undefined}
                  options={[
                    { label: "Kişi", value: "M" },
                    { label: "Qadın", value: "F" },
                  ]}
                />
                {editErrors.gender && <span className={styles.field_error}>{editErrors.gender[0]}</span>}
              </div>

              <div className={`${styles.form_item} ${styles.full_span}`}>
                <label htmlFor="edit-branch">Filial</label>
                <Select
                  id="edit-branch"
                  labelInValue
                  value={editSelectedBranch || undefined}
                  onChange={handleEditBranchChange}
                  placeholder="Filial seçin"
                  allowClear
                  showSearch
                  filterOption={false}
                  onSearch={handleSearchBranches}
                  loading={isBranchesLoading}
                  status={editErrors.branch_id ? "error" : undefined}
                  className={styles.form_select}
                  notFoundContent={isBranchesLoading ? "Yüklənir..." : "Filial tapılmadı"}
                  options={branchesList.map((branch) => ({
                    label: formatBranchLabel(branch),
                    value: String(branch.id),
                  }))}
                />
                {editErrors.branch_id && <span className={styles.field_error}>{editErrors.branch_id[0]}</span>}
              </div>

              <div className={`${styles.form_item} ${styles.full_span}`}>
                <label htmlFor="edit-address">Ünvan</label>
                <input
                  id="edit-address"
                  type="text"
                  value={editFormValues.address}
                  onChange={(e) => handleEditInputChange("address", e.target.value)}
                  placeholder="Ünvan"
                  className={`${styles.form_input} ${editErrors.address ? styles.input_error : ""}`}
                  tabIndex={0}
                />
                {editErrors.address && <span className={styles.field_error}>{editErrors.address[0]}</span>}
              </div>
            </div>

            <div className={styles.form_footer}>
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className={styles.cancel_btn}
                tabIndex={0}
                aria-label="İmtina et"
              >
                İmtina
              </button>
              <Tooltip title={!hasFormChanged ? "Heç bir dəyişiklik edilməyib" : ""}>
                <span style={{ display: "inline-block" }}>
                  <button
                    type="submit"
                    disabled={!hasFormChanged || isSavingEdit}
                    className={styles.save_btn}
                    tabIndex={0}
                    aria-label="Yadda saxla"
                  >
                    {isSavingEdit ? "Yadda saxlanılır..." : "Yadda saxla"}
                  </button>
                </span>
              </Tooltip>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default Users;
